import { createElement, isValidElement, useEffect, useRef } from 'react'

// Typeahead for option lists (ListboxPicker without a search box, the
// SelectMenu trigger): typing letters jumps to the next option whose text
// starts with them, as in a native <select>.

// An option's plain text: `text` when given, else a string or number label;
// '' when it has neither (a JSX label).
export function optionPlainText(option) {
  if ((typeof option.text === 'string' && option.text) || typeof option.text === 'number') return String(option.text)
  return typeof option.label === 'string' || typeof option.label === 'number' ? String(option.label) : ''
}

// The text an option is matched by: its plain text, lowercased.
export function optionText(option) {
  return optionPlainText(option).toLowerCase()
}

// An option's icon: an element as it is, a component rendered at 16px.
export function renderOptionIcon(icon, extraProps) {
  if (isValidElement(icon)) return icon
  return icon ? createElement(icon, { size: 16, ...extraProps }) : null
}

// The first index from `start`, stepping by `step` and wrapping, whose option
// satisfies `predicate`; -1 when none does.
export function scanOptions(options, start, step, predicate) {
  for (let offset = 0; offset < options.length; offset += 1) {
    const index = (((start + offset * step) % options.length) + options.length) % options.length
    if (predicate(options[index])) return index
  }
  return -1
}

// A printable character with no modifier. Space is left out: it picks.
export function isTypeaheadKey(event) {
  return event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey
}

// The index of the enabled option matching `search`, looking from `from`
// (the highlighted option) and wrapping; -1 when none does. One letter, or the
// same letter repeated, steps to the next option starting with it; a longer
// search keeps the highlighted option while it still matches.
export function findTypeaheadIndex(options, search, from) {
  if (!search || !options.length) return -1
  const repeated = [...search].every((char) => char === search[0])
  const needle = repeated ? search[0] : search
  const start = repeated || from < 0 ? from + 1 : from
  return scanOptions(options, start, 1, (option) => !option.disabled && optionText(option).startsWith(needle))
}

// Collects the letters typed in quick succession (half a second apart at
// most): call it with each key, it returns the search so far.
export function useTypeahead(timeout = 500) {
  const buffer = useRef('')
  const timer = useRef(null)
  useEffect(() => () => clearTimeout(timer.current), [])
  return (key) => {
    clearTimeout(timer.current)
    buffer.current += key.toLowerCase()
    timer.current = setTimeout(() => { buffer.current = '' }, timeout)
    return buffer.current
  }
}
